import {
    FilterExpression,
    FilterExpressionInput,
    FilterExpressionSelect,
    getSelectValues,
    ListBoxItem,
    useFilterExpressionInput,
    useFilterExpressionSelect,
    useNumberMask,
} from '@direct-frontend/components';
import { useCallback, useMemo, useRef } from 'react';
import { useLogger } from '@direct-frontend/modules-logger';
import { validateRangeFilterExpression } from './validate/validateRangeFilterExpression';
import { OperatorSet, OperatorType } from '../../lib/types/Operator';
import { BaseFilterProps, Filter, RangeFilterProps } from '../../lib/types/Filters';
import { useLogErrors } from '../../lib/hooks/useLogErrors';
import { isValidOperator } from '../../lib/helpers/reconcile/isValidOperator';
import { getOperatorTitle } from '../../lib/helpers/getOperatorTitle';

export const operators = {
    all: [
        { content: '=', value: '=' },
        { content: '≠', value: '!=' },
        { content: '>=', value: '>=' },
        { content: '<=', value: '<=' },
    ],
    equals: [
        { content: '=', value: '=' },
        { content: '≠', value: '!=' },
    ],
    compares: [
        { content: '>=', value: '>=' },
        { content: '<=', value: '<=' },
    ],
} satisfies Record<OperatorSet, ListBoxItem<OperatorType, OperatorType>[]>;

export function NumberRangeFilterExpression(
    props: Omit<RangeFilterProps<string>, 'maskConfig'> & {
        /**
         * Количество знаков после запятой
         */
        precision: number;
    }
) {
    const { precision, ...otherProps } = props;
    const numberMask = useNumberMask({
        precision,
        min: 0,
    });

    return <RangeFilterExpression {...otherProps} maskConfig={numberMask} />;
}

export function RangeFilterExpression({
    id,
    value,
    subFilterId,
    operator,
    availableOperators = 'all',
    subFilterIds,
    subFilterPlaceholder,
    onChange,
    onRemove,
    color = 'normal',
    title,
    addonLeft,
    errors,
    wasAutoFocused,
    maskConfig,
    'data-testid': dataTestId = 'Filters',
}: RangeFilterProps<string>) {
    const nodeInfo = useMemo(
        () => ({
            name: 'RangeFilterExpression',
            attrs: {
                id,
            },
        }),
        [id]
    );
    const { LogProvider, logTech } = useLogger(nodeInfo);

    const inputRef = useRef<HTMLTextAreaElement>(null);
    const hasSubFilters = subFilterIds && subFilterIds.length > 0;
    const { focus: inputFocus } = useFilterExpressionInput({
        ref: inputRef,
    });

    const operatorsItems = useMemo(() => operators?.[availableOperators], [availableOperators]);

    const onChangeSubFilter = useCallback(
        (subFilterIdValue: string) =>
            onChange({
                subFilterId: subFilterIdValue,
                value,
                operator,
            }),
        [onChange, operator, value]
    );
    const onChangeOperator = useCallback(
        (operatorValue: OperatorType) => onChange({ subFilterId, value, operator: operatorValue }),
        [onChange, subFilterId, value]
    );

    const onChangeValue = useCallback(
        (inputValue: string) => onChange({ subFilterId, value: inputValue, operator }),
        [onChange, operator, subFilterId]
    );

    const operatorProps = useFilterExpressionSelect({
        value: operator,
        onChange: onChangeOperator,
        onFocusNext: inputFocus,
        autoOpen: !wasAutoFocused && !hasSubFilters,
    });

    const subFiltersProps = useFilterExpressionSelect({
        value: subFilterId,
        onChange: onChangeSubFilter,
        onFocusNext: operatorProps.onOpen,
        autoOpen: !wasAutoFocused && !value,
    });

    useLogErrors(id, { errors, logTech });

    return (
        <LogProvider>
            <FilterExpression
                color={color}
                size="xs"
                data-testid={dataTestId}
                title={title}
                addonLeft={addonLeft}
                onRemove={onRemove}
                error={errors?.join('. ')}
                logNode={'RangeFilterExpression'}
            >
                {hasSubFilters && (
                    <FilterExpressionSelect
                        {...subFiltersProps}
                        data-testid={`${dataTestId}.subFilterSelect`}
                        placeholder={subFilterPlaceholder}
                        items={subFilterIds}
                        logNode="RangeFilterSubFilterSelector"
                    />
                )}
                <FilterExpressionSelect<OperatorType>
                    {...operatorProps}
                    data-testid={`${dataTestId}.operatorSelect`}
                    placeholder={'-'}
                    items={operatorsItems}
                    logNode="RangeFilterOperatorSelector"
                />
                <FilterExpressionInput
                    data-testid={`${dataTestId}.input`}
                    ref={inputRef}
                    value={value}
                    onChange={onChangeValue}
                    onRemove={onRemove}
                    maskConfig={maskConfig}
                    logNode="RangeFilterInput"
                />
            </FilterExpression>
        </LogProvider>
    );
}

/**
 * Пропсы для создания фильтра выражения
 * @typeParam FilterId Идентификатор фильтра
 * @typeParam ValueType Тип значения фильтра
 */
export interface CreateRangeFilterProps<FilterId extends string, ValueType extends string>
    extends BaseFilterProps<FilterId> {
    /** Доп критерии */
    items?: ListBoxItem<ValueType, string>[];
    /** Выбранный набор операторов */
    availableOperators?: OperatorSet;
    /**
     * Максимальное количество знаков после запятой
     *
     * @default 0
     */
    precision?: number;
}

/**
 * Фабрика, создающая фильтр выражение, в котором можно добавлять цель, оператор и значение,
 * и который можно передать как Filter в FiltersDeclaration.
 * @typeParam FilterId идентификатор фильтра
 * @typeParam ValueType тип значения фильтра
 * @returns Декларация фильтра
 * @kind helper
 */
export function createRangeFilter<FilterId extends string, ValueType extends string>({
    id,
    items,
    availableOperators,
    precision = 0,
    ...props
}: CreateRangeFilterProps<FilterId, ValueType>): Filter<FilterId, string> {
    const subFilterId = items && getFirstListValue(items);

    return {
        ...props,
        id,
        reconcile: ({ value, operator, subFilterId }) => {
            const filteredValue = value.filter(item => item !== '' && !Number.isNaN(Number(item)));

            const availableOperatorsArray = availableOperators && getSelectValues(operators[availableOperators]);

            if (!availableOperatorsArray || !isValidOperator(operator, availableOperatorsArray)) {
                return null;
            }

            const firstItem = filteredValue[0];

            if (!firstItem) {
                return null;
            }

            return {
                value: firstItem,
                operator,
                subFilterId,
            };
        },
        validate: validateRangeFilterExpression,
        render: props => (
            <NumberRangeFilterExpression
                {...props}
                subFilterIds={items}
                availableOperators={availableOperators}
                id={id}
                precision={precision}
            />
        ),
        onlyOneFilterItem: false,
        hasValue: ({ value }) => value.length > 0,
        getDefaultValue: availableOperators => ({
            value: '',
            operator: availableOperators[0],
            subFilterId,
        }),
        getDescription: ({ operator = '=', value }) => `${props.title} ${getOperatorTitle(operator)} ${value}`,
        operators: operators[availableOperators ?? 'all'].map(({ value }) => value),
    };
}

function getFirstListValue<T extends string>(items: ListBoxItem<T, string>[]): T | undefined {
    for (const item of items) {
        let value: T | undefined;
        if ('options' in item) {
            value = getFirstListValue(item.options);
        } else {
            value = item.value;
        }

        if (value) {
            return value;
        }
    }
    return undefined;
}
